import { useMemo, useState } from 'react'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded'
import type { ChecklistItem } from '../../types/schema'
import type { RecurringMode } from '../../types/recurringChecklist'
import {
  advanceDate,
  occurrenceKeyFor,
  parseIsoDate,
  startOfIsoWeek,
} from '../../services/recurringChecklistService'
import { useOccurrenceHistory } from './useOccurrenceHistory'
import { deriveChecklistTimeStatus } from '../../utils/checklistTimeStatus'
import { getCachedTodosLeadDays } from '../../services/workspaceTodosLeadDaysCache'
import { useNls } from '../../nls/nlsProvider'
import { useWorkspaceDay } from '../../hooks/useWorkspaceDay'
import {
  getChecklistCadenceLabel,
  getChecklistOccurrenceHistoryLabel,
  getChecklistOccurrenceTodayLabel,
  type ChecklistNlsT,
} from './ChecklistNls'
import { StatusChip } from '../Common'
import { IconButton } from '../Common/IconButton'
import { Pressable } from '../Common/Pressable'

type Props = {
  item: ChecklistItem
  /** The mode this chip is anchored to. On Main canvas defaults to the item's
   *  own checklistType; on Daily/Weekly canvas matches the canvas mode. */
  mode?: RecurringMode
  /** The focused occurrence key. When absent, defaults to today's key for
   *  the mode. Determines the chip label + which occurrence is "current". */
  focusedKey?: string
  /** Called when the user picks a different occurrence date from the chip
   *  dropdown. Recieves a Date at midnight local. */
  onJump?: (date: Date) => void
}

const RANGE = 6 // Fallback ±6 for legacy items without a recurringWindow.
/** Cap the rendered occurrence list so extremely long parent windows
 *  (multi-year Goals) don't render tens of thousands of rows. The user can
 *  still navigate to any date via the canvas paginator. */
const MAX_OCCURRENCE_ROWS = 366

const labelForKey = (mode: RecurringMode, key: string, todayKey: string, t: ChecklistNlsT): string => {
  if (key === todayKey) return getChecklistOccurrenceTodayLabel(t, mode)
  if (mode === 'DAILY') {
    const date = parseIsoDate(key)
    return date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
  }
  // Weekly key: "YYYY-Www" — display as week-range short form.
  const [y, w] = key.split('-W').map(Number)
  if (!y || !w) return key
  const jan4 = new Date(y, 0, 4)
  const week1Monday = startOfIsoWeek(jan4)
  const target = new Date(week1Monday)
  target.setDate(target.getDate() + (w - 1) * 7)
  const end = new Date(target)
  end.setDate(end.getDate() + 6)
  const fmt = (v: Date) => v.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  return `${fmt(target)}–${fmt(end)}`
}

const dateForKey = (mode: RecurringMode, key: string): Date => {
  if (mode === 'DAILY') return parseIsoDate(key)
  const [y, w] = key.split('-W').map(Number)
  const jan4 = new Date(y, 0, 4)
  const week1Monday = startOfIsoWeek(jan4)
  const target = new Date(week1Monday)
  target.setDate(target.getDate() + (w - 1) * 7)
  return target
}

/** Compact date chip rendered on a recurring item. Shows the focused
 *  occurrence (or "Today"), and clicking opens a mini list of past/future
 *  occurrences with a tick indicator for each. Selecting one calls onJump. */
export const RecurringOccurrenceChip = ({ item, mode, focusedKey, onJump }: Props) => {
  const { t } = useNls()
  const [open, setOpen] = useState(false)
  const resolvedType = item.checklistType ?? 'INDIVIDUAL'
  const resolvedMode: RecurringMode = mode ?? (resolvedType === 'WEEKLY' ? 'WEEKLY' : 'DAILY')

  const { timeZone: workspaceTimezone } = useWorkspaceDay(item.workspaceId)
  const today = new Date()
  const todayKey = occurrenceKeyFor(resolvedMode, today, workspaceTimezone)
  const activeKey = focusedKey ?? todayKey
  const activeLabel = labelForKey(resolvedMode, activeKey, todayKey, t)

  // Parent-scoped recurring: walk from window.startDate → window.endDate so
  // the drawer surfaces every occurrence the item actually covers. Legacy
  // items without a window still get the ±6-around-today range so nothing
  // regresses. Capped at MAX_OCCURRENCE_ROWS for multi-year parents.
  const windowStartDate = item.recurringWindow?.startDate ?? null
  const windowEndDate = item.recurringWindow?.endDate ?? null
  const historyKeys = useMemo(() => {
    if (resolvedType === 'INDIVIDUAL') return []
    const keys: string[] = []
    if (windowStartDate && windowEndDate) {
      const start = resolvedMode === 'WEEKLY'
        ? startOfIsoWeek(parseIsoDate(windowStartDate.slice(0, 10)), workspaceTimezone)
        : parseIsoDate(windowStartDate.slice(0, 10))
      const end = parseIsoDate(windowEndDate.slice(0, 10))
      let cursor = new Date(start)
      cursor.setHours(0, 0, 0, 0)
      const stopMs = end.getTime()
      const seen = new Set<string>()
      while (cursor.getTime() <= stopMs && keys.length < MAX_OCCURRENCE_ROWS) {
        const key = occurrenceKeyFor(resolvedMode, cursor, workspaceTimezone)
        if (!seen.has(key)) { seen.add(key); keys.push(key) }
        cursor = advanceDate(resolvedMode, cursor, 1)
      }
      // Always include today's key so the "Today" indicator anchors even
      // when today lies outside the window (past-dated parent).
      const currentTodayKey = occurrenceKeyFor(resolvedMode, new Date(), workspaceTimezone)
      if (!seen.has(currentTodayKey) && keys.length < MAX_OCCURRENCE_ROWS) {
        // Insert in sorted order for DAILY / WEEKLY (both string-comparable).
        const insertIdx = keys.findIndex((k) => k > currentTodayKey)
        if (insertIdx < 0) keys.push(currentTodayKey)
        else keys.splice(insertIdx, 0, currentTodayKey)
      }
      return keys
    }
    for (let i = -RANGE; i <= RANGE; i += 1) {
      const target = advanceDate(resolvedMode, today, i)
      const key = occurrenceKeyFor(resolvedMode, target, workspaceTimezone)
      keys.push(key)
    }
    return keys
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedMode, windowStartDate, windowEndDate, resolvedType, workspaceTimezone])

  // Lazy history subscription — costs nothing until the popover opens.
  // On the Recurring canvas the projected `item.occurrences` already
  // carries the focused key's entry, but any other surface (Main
  // canvas, Board Explorer, ForYou, Member surfaces) only sees today's
  // entry from TimelineContext's book-model subscription. This hook
  // fills every past + future occurrence with the real completion
  // state so the tick indicator on each history row is truthful.
  const { history } = useOccurrenceHistory({
    itemId: item.id,
    workspaceId: item.workspaceId,
    mode: resolvedMode,
    enabled: open && resolvedType !== 'INDIVIDUAL',
  })

  if (resolvedType === 'INDIVIDUAL') return null

  const handlePick = (key: string) => {
    setOpen(false)
    if (!onJump) return
    onJump(dateForKey(resolvedMode, key))
  }

  return (
    <span className="recOccChip">
      <Pressable
        className={`recOccChip__btn${activeKey === todayKey ? ' is-today' : ''}`}
        onClick={(event) => { event.stopPropagation(); setOpen((v) => !v) }}
        title={`${getChecklistCadenceLabel(t, resolvedMode)} · ${activeLabel}`}
      >
        <span className="recOccChip__dot" aria-hidden />
        <span className="recOccChip__label">{activeLabel}</span>
      </Pressable>
      {Boolean(item.repeatPaused) && (
        <StatusChip tone="warning" size="sm" className="recOccChip__pausedChip">
          {t('NLSChecklistRepeatPausedParentDates')}
        </StatusChip>
      )}
      {open && (
        <div
          className="recOccChip__pop"
          role="listbox"
          onClick={(event) => event.stopPropagation()}
        >
          <header className="recOccChip__popHead">
            <span>{getChecklistOccurrenceHistoryLabel(t, resolvedMode)}</span>
            <IconButton
              variant="tertiary"
              size="sm"
              className="recOccChip__popClose"
              label={t('NLSChecklistActionClose')}
              icon={<CloseRoundedIcon fontSize="inherit" />}
              onClick={() => setOpen(false)}
            />
          </header>
          <ul className="recOccChip__list">
            {historyKeys.map((key) => {
              // Prefer the live book-page snapshot; fall back to the
              // projected `occurrences` map while the hook's first
              // snapshot is in flight (or when the item has no
              // workspaceId, e.g. preview data).
              const entry = history?.[key] ?? item.occurrences?.[key]
              const done = Boolean(entry?.isCompleted)
              const isActive = key === activeKey
              const isToday = key === todayKey
              // Per-occurrence time status: DAILY treats the day, WEEKLY
              // treats the Monday of the week as start. Falls back to
              // 'active' when no window / lead-days available.
              const occurrenceDate = dateForKey(resolvedMode, key)
              const occurrenceIso = `${occurrenceDate.getFullYear().toString().padStart(4, '0')}-${(occurrenceDate.getMonth() + 1).toString().padStart(2, '0')}-${occurrenceDate.getDate().toString().padStart(2, '0')}`
              const occurrenceTimeStatus = item.recurringWindow
                ? deriveChecklistTimeStatus({
                    scheduleMode: resolvedMode === 'WEEKLY' ? 'DURATION' : 'DATED',
                    scheduleStartDate: occurrenceIso,
                    scheduleEndDate: resolvedMode === 'WEEKLY'
                      ? `${occurrenceDate.getFullYear().toString().padStart(4, '0')}-${(occurrenceDate.getMonth() + 1).toString().padStart(2, '0')}-${(occurrenceDate.getDate() + 6).toString().padStart(2, '0')}`
                      : occurrenceIso,
                    leadDays: getCachedTodosLeadDays(item.workspaceId),
                  })
                : 'active'
              return (
                <li key={key}>
                  <Pressable
                    className={`recOccChip__row${isActive ? ' is-active' : ''}${isToday ? ' is-today' : ''} recOccChip__row--${occurrenceTimeStatus}`}
                    onClick={() => handlePick(key)}
                    role="option"
                    aria-selected={isActive}
                    title={occurrenceTimeStatus === 'archived'
                      ? t('NLSChecklistOccurrenceArchivedTooltip')
                      : occurrenceTimeStatus === 'upcoming'
                        ? t('NLSChecklistOccurrenceUpcomingTooltip')
                        : undefined}
                  >
                    <span className="recOccChip__rowStatus" aria-hidden>
                      {done
                        ? <CheckCircleRoundedIcon fontSize="inherit" />
                        : <RadioButtonUncheckedRoundedIcon fontSize="inherit" />}
                    </span>
                    <span className="recOccChip__rowLabel">{labelForKey(resolvedMode, key, todayKey, t)}</span>
                    {isToday && <StatusChip size="sm" tone="info">{t('NLSChecklistOccurrenceToday')}</StatusChip>}
                    {!isToday && occurrenceTimeStatus === 'archived' && (
                      <StatusChip size="sm" tone="muted">{t('NLSChecklistOccurrenceArchived')}</StatusChip>
                    )}
                    {!isToday && occurrenceTimeStatus === 'upcoming' && (
                      <StatusChip size="sm" tone="neutral">{t('NLSChecklistOccurrenceUpcoming')}</StatusChip>
                    )}
                  </Pressable>
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </span>
  )
}
